import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { ContactSupportLink } from "@bokio/components/ContactSupportLink/ContactSupportLink";
import { helpArticleAliases, HelpLink } from "@bokio/components/HelpLink/HelpLink";
import { Heading, Paragraph, TextSpan } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { TypoGraphicElementInternals } from "@bokio/designsystem/components/TypographicElements/TypographicElements.internals";
import { Link } from "@bokio/elements/Link/Link";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { mergeClassNames } from "@bokio/utils/classes";
import { trackError, trackIfContainsHtmlEncodedText } from "@bokio/utils/t";
import { getCompanyIdFromUrl } from "@bokio/utils/url";

import { Table, TBody, Td, Th, THead, Tr } from "../Table";

import type { HelpArticleAlias, HelpLinkProps } from "@bokio/components/HelpLink/HelpLink";
import type { LinkProps } from "@bokio/elements/Link/Link";
import type { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import type { Components } from "react-markdown";

import * as interopStyles from "./markdownTypographicElementsInterop.scss";

interface MarkdownProps {
	markdownContent: string;
	testId?: string;
	/**
	 * If true, the Markdown content will not be wrapped in a <div> element.
	 *
	 * @default false
	 */
	noWrappingDiv?: boolean;
	/**
	 * Instead of rendering Markdown paragraph as <Paragraph> (<p>), render it using <TextSpan> (<span>) instead.
	 *
	 * This is mainly for the case when you only need some simple inline text with the markdown style.
	 *
	 * Implies {@link MarkdownProps.noWrappingDiv} when true.
	 *
	 * @default false
	 */
	inline?: boolean;
	/**
	 * @deprecated Markdown component use the typographic elements by default, existing use cases should be migrated by removing this prop and fix so it still fits into the page's design.
	 */
	deprecatedOptoutFromTypographicElements?: boolean;
	/**
	 * Applies class name to each of the native HTML elements when {@link MarkdownProps.deprecatedOptoutFromTypographicElements} is true.
	 *
	 * @deprecated This should be removed as well as {@link MarkdownProps.deprecatedOptoutFromTypographicElements}.
	 */
	deprecatedNativeHtmlElementClassName?: string;
}

const createInternalLinkProps = (path: string, locationPathName: string): LinkProps => {
	const placeholder = "/companyid/";
	let adjustedPath = path;
	if (path.indexOf(placeholder) >= 0 && locationPathName) {
		const companyid = getCompanyIdFromUrl(locationPathName);
		if (companyid) {
			adjustedPath = path.replace(placeholder, `/${companyid}/`);
		}
	}
	return { route: adjustedPath };
};

const createInternalHelpLinkProps = (helpArticle: string): HelpLinkProps | undefined => {
	if (!helpArticleAliases.map(a => a.toString()).includes(helpArticle)) {
		return undefined;
	}

	return { helpArticle: helpArticle as HelpArticleAlias };
};

const renderHelpLink = ({
	helpArticle,
	children,
	key,
}: {
	helpArticle: string;
	children: ReactNode;
	key: string | number | undefined;
}) => {
	const refProps = createInternalHelpLinkProps(helpArticle);
	if (!refProps) {
		// If the helpArticle doesn't exist, log an error and return a span with the link anchor text
		trackError("HelpLink helpArticle from language string in Markdown doesn't exist", "Markdown", { helpArticle });
		return <span key={key}>{children}</span>;
	}

	return (
		<HelpLink key={key} {...refProps}>
			{children}
		</HelpLink>
	);
};

const renderSupportLink = ({
	area,
	children,
	key,
}: {
	area: string;
	children: ReactNode;
	key: string | number | undefined;
}) => {
	const supportAreas = m.Contracts.SupportFormArea as unknown as Record<string, m.Contracts.SupportFormArea>;

	if (!supportAreas[area]) {
		// If area is not in SupportFormArea, it will be undefined.
		trackError("Support link area from language string in Mardown doesn't exist", "Markdown", { area });
	}

	return (
		<ContactSupportLink key={key} area={supportAreas[area] ?? m.Contracts.SupportFormArea.NotSet}>
			{children}
		</ContactSupportLink>
	);
};

const renderLink = ({
	href,
	locationPathName,
	children,
	deprecatedNativeHtmlElementClassName,
	key,
}: {
	href: string;
	locationPathName: string;
	children: ReactNode;
	deprecatedNativeHtmlElementClassName: MarkdownProps["deprecatedNativeHtmlElementClassName"];
	key: string | number | undefined;
}) => {
	const isInternal = href.startsWith("bokio:");
	const refProps: LinkProps = isInternal
		? createInternalLinkProps(href.substring(6), locationPathName)
		: { external: href, target: "_blank" };

	return (
		<Link
			key={key}
			{...refProps}
			className={deprecatedNativeHtmlElementClassName}
			onClick={e => {
				e.stopPropagation();
			}}
		>
			{children}
		</Link>
	);
};

const renderMarkdown = ({
	markdown,
	deprecatedNativeHtmlElementClassName,
	deprecatedOptoutFromTypographicElements,
	inline,
	testId,
	locationPathName,
}: {
	markdown: string;
	deprecatedNativeHtmlElementClassName: MarkdownProps["deprecatedNativeHtmlElementClassName"];
	deprecatedOptoutFromTypographicElements: MarkdownProps["deprecatedOptoutFromTypographicElements"];
	inline: boolean | undefined;
	testId: string | undefined;
	locationPathName: string;
}) => {
	const useTypographicElements = !deprecatedOptoutFromTypographicElements;
	const interopClassName = useTypographicElements ? interopStyles.interop : undefined;

	const components: Components = {
		a: props => {
			const href = props.href || "";
			const bokioHelpPrefix = "bokio-help:/";
			const bokioSupportLinkPrefix = "bokio-support:";
			const isInternalHelpLink = href.startsWith(bokioHelpPrefix);
			const isSupportLink = href.startsWith(bokioSupportLinkPrefix);

			if (isInternalHelpLink) {
				return renderHelpLink({
					helpArticle: href.substring(bokioHelpPrefix.length),
					children: props.children,
					key: undefined,
				});
			}
			if (isSupportLink) {
				return renderSupportLink({
					area: href.substring(bokioSupportLinkPrefix.length),
					children: props.children,
					key: undefined,
				});
			}
			return renderLink({
				href,
				locationPathName,
				deprecatedNativeHtmlElementClassName,
				children: props.children,
				key: undefined,
			});
		},
		p: props => {
			if (useTypographicElements) {
				if (inline) {
					return <TextSpan testId={testId}>{props.children}</TextSpan>;
				}
				return <Paragraph>{props.children}</Paragraph>;
			}
			if (inline) {
				return (
					<span data-testid={testId} className={deprecatedNativeHtmlElementClassName}>
						{props.children}
					</span>
				);
			}
			return <p className={deprecatedNativeHtmlElementClassName}>{props.children}</p>;
		},
		strong: props => {
			if (useTypographicElements) {
				return <TextSpan style="body-bold">{props.children}</TextSpan>;
			}
			return <strong className={deprecatedNativeHtmlElementClassName}>{props.children}</strong>;
		},
		h1: props =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={1}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h1 className={deprecatedNativeHtmlElementClassName}>{props.children}</h1>
			),
		h2: props =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={2}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h2 className={deprecatedNativeHtmlElementClassName}>{props.children}</h2>
			),
		h3: props =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={3}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h3 className={deprecatedNativeHtmlElementClassName}>{props.children}</h3>
			),
		h4: props =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={4}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h4 className={deprecatedNativeHtmlElementClassName}>{props.children}</h4>
			),
		h5: props =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={5}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h5 className={deprecatedNativeHtmlElementClassName}>{props.children}</h5>
			),
		h6: (props: ComponentPropsWithoutRef<"h6"> & { node?: unknown }) =>
			useTypographicElements ? (
				<TypoGraphicElementInternals.HeadingLevel bumpLevelBy={6}>
					<Heading>{props.children}</Heading>
				</TypoGraphicElementInternals.HeadingLevel>
			) : (
				<h6 className={deprecatedNativeHtmlElementClassName}>{props.children}</h6>
			),
		ul: props => (
			<ul className={mergeClassNames(interopClassName, deprecatedNativeHtmlElementClassName)}>{props.children}</ul>
		),
		ol: props => (
			<ol className={mergeClassNames(interopClassName, deprecatedNativeHtmlElementClassName)}>{props.children}</ol>
		),
		li: props => (
			<li className={mergeClassNames(interopClassName, deprecatedNativeHtmlElementClassName)}>{props.children}</li>
		),
		table: props => <Table>{props.children}</Table>,
		tbody: props => <TBody>{props.children}</TBody>,
		thead: props => <THead>{props.children}</THead>,
		tr: props => <Tr>{props.children}</Tr>,
		th: props => <Th>{props.children}</Th>,
		td: props => <Td>{props.children}</Td>,
	};

	return (
		<ReactMarkdown
			components={components}
			urlTransform={url => {
				if (/^(http|https|mailto|bokio|bokio-help|bokio-support):/.test(url)) {
					return url;
				}
				return null;
			}}
			remarkPlugins={[remarkGfm, remarkBreaks]}
		>
			{markdown}
		</ReactMarkdown>
	);
};

/**
 * Renders Markdown to React components. Doesn't ever render raw html.
 * For links, you can use the special url syntax bokio:/companyid/path to generate internal react links
 * where the companyid is replaced with the current company guid
 */
const Markdown: FC<MarkdownProps> = ({
	testId,
	markdownContent,
	deprecatedNativeHtmlElementClassName,
	deprecatedOptoutFromTypographicElements,
	noWrappingDiv,
	inline,
}) => {
	const router = useRouter();
	useEffect(() => {
		trackIfContainsHtmlEncodedText(markdownContent, "Markdown");
	}, [markdownContent]);

	const markdown = () =>
		renderMarkdown({
			markdown: markdownContent,
			deprecatedNativeHtmlElementClassName,
			deprecatedOptoutFromTypographicElements,
			inline,
			testId,
			locationPathName: router.location?.pathname || "/",
		});

	const rendered = noWrappingDiv || inline ? <>{markdown()}</> : <div data-testid={testId}>{markdown()}</div>;

	return rendered;
};

export default Markdown;
